import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, Divider, Typography } from '@mui/material';
import { useGoogleSignIn } from '../../hooks/useGoogleSignIn';

// Google's rendered button accepts a width of 200-400px.
const MIN_WIDTH = 200;
const MAX_WIDTH = 400;

/**
 * Google's own "Sign in with Google" button (Google Identity Services).
 * clientId comes from useLoginOptions; renders nothing without one. With
 * showDivider an "or" divider follows it (for the password form below).
 * onCredential receives the Google ID token string.
 */
const GoogleSignInButton = ({ clientId, onCredential, disabled, showDivider }) => {
  const { ready } = useGoogleSignIn(clientId);
  const buttonRef = useRef(null);
  // GIS keeps the first callback it is given; route through a ref so it
  // always calls the latest onCredential.
  const onCredentialRef = useRef(onCredential);
  onCredentialRef.current = onCredential;

  useEffect(() => {
    const gsi = window.google?.accounts?.id;
    if (!ready || !clientId || !gsi || !buttonRef.current) return;
    gsi.initialize({
      client_id: clientId,
      callback: (response) => {
        if (response?.credential) onCredentialRef.current(response.credential);
      },
      ux_mode: 'popup',
    });
    const width = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, buttonRef.current.offsetWidth || MIN_WIDTH));
    gsi.renderButton(buttonRef.current, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      text: 'signin_with',
      shape: 'rectangular',
      logo_alignment: 'center',
      width,
      // The app is English-only; without this Google localises the button per
      // visitor (browser / Google account language).
      locale: 'en',
    });
  }, [ready, clientId]);

  if (!clientId) return null;

  return (
    <Box sx={{ mb: 1 }}>
      <Box
        ref={buttonRef}
        data-testid="google-signin-button"
        sx={{
          width: '100%',
          minHeight: 44,
          display: 'flex',
          justifyContent: 'center',
          // The iframe Google injects can't be disabled; block clicks instead.
          pointerEvents: disabled ? 'none' : 'auto',
          opacity: disabled ? 0.6 : 1,
        }}
      />
      {showDivider && (
        <Divider sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            or
          </Typography>
        </Divider>
      )}
    </Box>
  );
};

GoogleSignInButton.propTypes = {
  clientId: PropTypes.string,
  onCredential: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  showDivider: PropTypes.bool,
};

GoogleSignInButton.defaultProps = {
  clientId: null,
  disabled: false,
  showDivider: true,
};

export default GoogleSignInButton;
